import type { HTMLAttributes } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import {
  FIELD_LABEL_CLASS,
  FULL_ROW_FIELD_CLASS,
  LEADING_SYMBOL_CLASS,
} from './formDialog.constants';

interface FormTextFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  placeholder?: string | undefined;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'] | undefined;
  leadingSymbol?: string | undefined;
  hasTabularNumbers?: boolean | undefined;
  isFullRow?: boolean | undefined;
}

export function FormTextField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  inputMode,
  leadingSymbol,
  hasTabularNumbers = false,
  isFullRow = false,
}: FormTextFieldProps<TValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(isFullRow && FULL_ROW_FIELD_CLASS)}>
          <FormLabel className={FIELD_LABEL_CLASS}>{label}</FormLabel>
          <div className="relative">
            {leadingSymbol === undefined ? null : (
              <span className={LEADING_SYMBOL_CLASS} aria-hidden>
                {leadingSymbol}
              </span>
            )}
            <FormControl>
              <Input
                {...field}
                placeholder={placeholder}
                inputMode={inputMode}
                autoComplete="off"
                className={cn(
                  FIELD_CONTROL_CLASS,
                  leadingSymbol !== undefined && 'pl-8',
                  hasTabularNumbers && 'tabular-nums',
                )}
              />
            </FormControl>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
